/**
 * @file Sign-in request-composition helpers for the marketing surface's
 *       inline `SignInCTA` capture island, grouped as `static` members on
 *       the `Authorize` class: the OAuth top-level-redirect builder
 *       (`Authorize.oauthRedirect`) and the email magic-link body builder
 *       (`Authorize.emailSigninRequest`). Both merge the browser signals
 *       the declarative island can't supply — the IANA timezone + the
 *       browser-classified acquisition — so the island stays declarative.
 * @module @labcraft/www/ui/authorize
 *
 * The email magic-link path still flows through the typed `Account`
 * Client (see `islands/signin_cta.tsx`); `Authorize.emailSigninRequest`
 * only composes its body from the browser signals — no transport lives
 * here. The two paths carry the same signals on different edges: query
 * params on the OAuth top-level navigation (no request body), typed body
 * properties on the email POST.
 *
 * OAuth same-origin against www's own Proxy mount: top-level
 * navigation to `/api/account/<provider>/authorize`. www's daemon
 * handles the full OAuth round-trip including the provider callback
 * at `/api/account/<provider>/callback`. The session cookie minted on
 * success is shared with lab via the parent-domain `Domain=` attribute
 * (prod) or host-only on `127.0.0.1` (dev), so the post-auth 302 to
 * lab arrives already authenticated.
 */

import { BrowserAcquisition, browserTimezone } from '@labcraft/core/frontend'
import type { EmailSigninRequest } from '@labcraft/account/proxy-routes'

/**
 * Static home for www's two sign-in request-composition helpers. Both
 * are pure browser-edge builders — they read `window` / `document` +
 * `Intl` and return a value, holding no instance state — so they sit as
 * `static` members on one class: a single discovery surface for "how the
 * `SignInCTA` composes a sign-in request from the browser signals".
 */
export class Authorize {
  /**
   * Build an OAuth provider-redirect handler for the `SignInCTA` island.
   * Same-origin top-level navigation — www's own Proxy mount handles the
   * OAuth round-trip end-to-end.
   *
   * OAuth is a top-level navigation with no request body, so the browser
   * signals ride query params: `tz` (IANA timezone) and the
   * browser-classified acquisition (`acq_source` / `acq_cat` /
   * `acq_campaign` — the campaign param only when a `utm_campaign` tag was
   * present). www's Proxy forwards them to the account daemon, which
   * re-validates and writes them to `learners.timezone` /
   * `learners.acquisition_*` on first sign-up. Raw referrer / utm never leave the browser — only
   * `BrowserAcquisition.read()`'s sanitized source + coarse category (the
   * persisted first-touch value, or a live classification when none is
   * stored), mirroring lab's own OAuth client (#1214).
   *
   * @param provider - OAuth provider id (`'github'` | `'google'`).
   * @returns Callback the `SignInCTA` invokes with an optional `next`
   *   param.
   */
  static oauthRedirect(provider: 'github' | 'google'): (next?: string) => void {
    return (next?: string) => {
      const params = new URLSearchParams()
      if (next) params.set('next', next)

      const tz = browserTimezone()
      if (tz) params.set('tz', tz)

      const acq = BrowserAcquisition.read()
      if (acq?.source) params.set('acq_source', acq.source)
      if (acq?.category) params.set('acq_cat', acq.category)
      if (acq?.campaign) params.set('acq_campaign', acq.campaign)

      const qs = params.toString()
      window.location.assign(
        `/api/account/${provider}/authorize${qs ? `?${qs}` : ''}`,
      )
    }
  }

  /**
   * Compose the typed `EmailSigninRequest` body for the magic-link path,
   * merging the browser signals the `SignInCTA` can't supply — the IANA
   * timezone + the browser-classified acquisition — onto the caller's
   * `{ email, next? }`. Sibling to {@link Authorize.oauthRedirect}: the
   * OAuth path carries `tz` / `acq_source` / `acq_cat` / `acq_campaign` as
   * query params, this path carries `timezone` / `acquisitionSource` /
   * `acquisitionCategory` / `acquisitionCampaign` as typed body properties.
   * Raw referrer / utm never leave the browser — only
   * `BrowserAcquisition.read()`'s sanitized source + coarse category +
   * campaign name (the persisted first-touch value, or a live
   * classification when none is stored), mirroring lab's own email-signin
   * hook (#1214).
   *
   * Kept out of the island (rather than inlined) so it is unit-testable
   * in www's no-jsdom style by stubbing `window` / `document`, exactly
   * like {@link Authorize.oauthRedirect} — the island's `submitEmail` is
   * a one-line delegation to this + `signinWithEmail`.
   *
   * @param args - The base request from the `SignInCTA` (`email`,
   *   optional `next`).
   * @returns The request with `timezone` + acquisition merged in.
   */
  static emailSigninRequest(args: EmailSigninRequest): EmailSigninRequest {
    const tz = browserTimezone()
    const acq = BrowserAcquisition.read()
    return {
      ...args,
      ...(tz ? { timezone: tz } : {}),
      ...(acq?.source ? { acquisitionSource: acq.source } : {}),
      ...(acq?.category ? { acquisitionCategory: acq.category } : {}),
      ...(acq?.campaign ? { acquisitionCampaign: acq.campaign } : {}),
    }
  }
}
